import { Fragment, type ReactNode } from 'react';
import { analitzaMarkdown, type Inline } from './markdown';

type RenderText = (text: string, clau: string) => ReactNode;

const textPla: RenderText = (text) => text;

function renderInline(nodes: Inline[], renderText: RenderText, prefix: string): ReactNode[] {
  return nodes.map((n, i) => {
    const clau = `${prefix}.${i}`;
    switch (n.tipus) {
      case 'text':
        return <Fragment key={clau}>{renderText(n.text, clau)}</Fragment>;
      case 'codi':
        return (
          <code key={clau} className="md__codi">
            {n.text}
          </code>
        );
      case 'negreta':
        return <strong key={clau}>{renderInline(n.fills, renderText, clau)}</strong>;
      case 'cursiva':
        return <em key={clau}>{renderInline(n.fills, renderText, clau)}</em>;
      case 'ratllat':
        return <s key={clau}>{renderInline(n.fills, renderText, clau)}</s>;
    }
  });
}

/**
 * Text d'un missatge amb el subconjunt de Markdown del xat, renderitzat com a elements React
 * (sense HTML injectat). `renderText` permet decorar els trossos de text pla (p. ex. mencions).
 * Els salts de línia d'un paràgraf es conserven com a «\n» (el contenidor usa `pre-wrap`).
 */
export function TextMarkdown({
  text,
  renderText = textPla,
}: {
  text: string;
  renderText?: RenderText;
}) {
  const blocs = analitzaMarkdown(text);
  return (
    <>
      {blocs.map((b, i) => {
        if (b.tipus === 'paragraf') {
          return (
            <span key={i} className="md__paragraf">
              {b.linies.map((l, j) => (
                <Fragment key={j}>
                  {j > 0 ? '\n' : null}
                  {renderInline(l, renderText, `${i}.${j}`)}
                </Fragment>
              ))}
            </span>
          );
        }
        const items = b.items.map((it, j) => (
          <li key={j}>{renderInline(it, renderText, `${i}.${j}`)}</li>
        ));
        return b.ordenada ? (
          <ol key={i} className="md__llista" start={b.inici === 1 ? undefined : b.inici}>
            {items}
          </ol>
        ) : (
          <ul key={i} className="md__llista">
            {items}
          </ul>
        );
      })}
    </>
  );
}
